import { type EventPerformer } from '#app/utils/providers/events.server.js'

/**
 * A concert as the public program shows it. Seat use is a count
 * (`ticketCount`) — public pages never carry ticket rows. Staff screens that
 * already hold full events (with `tickets`) fit this shape too.
 */
export type ProgramEvent = {
	id: string
	date: Date | string
	endsAt?: Date | string | null
	/** "berre møt opp": no ticket, no seats, no prices */
	dropIn?: boolean | null
	title: string
	description?: string | null
	shortDescription?: string | null
	performers?: string | null
	location?: string | null
	host?: string | null
	imageUrl?: string | null
	imageSafeZone?: string | null
	adultPrice?: number | null
	studentPrice?: number | null
	generousPrice?: number | null
	childPrice?: number | null
	seats?: { total: number } | null
	featuring?: EventPerformer[] | null
	ticketCount?: number | null
	tickets?: ReadonlyArray<{ paymentStatus?: string | null }> | null
}

// ", 4265 Håvik" — everyone at the festival is in the same postcode
const POSTCODE_SUFFIX = /,\s*\d{4}\s+[^,]+$/

/** "Vert — Adresse", or whichever of the two is set, without the postcode. */
export const venueLabel = (event: Pick<ProgramEvent, 'host' | 'location'>) =>
	[event.host?.trim(), event.location?.trim().replace(POSTCODE_SUFFIX, '')]
		.filter(Boolean)
		.join(' — ')

/**
 * Card blurb: the printed-program summary when there is one, otherwise the
 * description stripped of markdown and cut at a word boundary.
 */
export function eventSummary(
	event: Pick<ProgramEvent, 'shortDescription' | 'description'>,
	maxLength = 140,
) {
	const short = event.shortDescription?.trim()
	if (short) return short
	const text = (event.description ?? '')
		// images and link targets carry no reading value on a card
		.replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
		.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
		.replace(/[#>*_()![\]]/g, '')
		.replace(/^-{3,}$/gm, ' ')
		.replace(/\s+/g, ' ')
		.trim()
	if (text.length <= maxLength) return text
	const cut = text.slice(0, maxLength)
	const lastSpace = cut.lastIndexOf(' ')
	return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}
